import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import Popup from 'reactjs-popup';
import { SeatsioDesigner } from '@seatsio/seatsio-react';
import { AlertTriangle, Button, Save01, Spinner } from '@opendate-app/setlist';
import { toastError } from '../../../shared/toastHelper';

const SeatingChartDesignerModal = ({
  show,
  onSaveAndExit,
  isSaving=false,
  editorKeyUrl,
  chartKey,
  readOnlyName=false,
  hasUnsavedChanges=false,
  setHasUnsavedChanges,
  onChartCreated,
  onDiscard=null,
  notice=null,
  overlayZIndex=1060,
}) => {
  const [secretKey, setSecretKey] = useState(null);
  const [keyFailed, setKeyFailed] = useState(false);

  useEffect(() => {
    if (!show) return undefined;
    let active = true;
    setSecretKey(null);
    setKeyFailed(false);
    const fail = () => {
      if (!active) return;
      setKeyFailed(true);
      toastError('The seat map designer could not be opened. Try again shortly.');
    };
    axios.get(editorKeyUrl)
      .then(({ data }) => { if (!active) return; if (data && data.secretKey) { setSecretKey(data.secretKey); } else { fail(); } })
      .catch(fail);
    return () => { active = false; };
  }, [show, editorKeyUrl]);

  useEffect(() => {
    const handler = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    if (show) window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges, show]);

  return (
    <Popup
      open={show}
      onClose={onSaveAndExit}
      closeOnEscape={false}
      closeOnDocumentClick={false}
      modal
      overlayStyle={{ zIndex: overlayZIndex }}
      contentStyle={{
        width: '95vw',
        height: '95vh',
        padding: '0px',
        borderRadius: '8px',
        border: 'none',
        overflow: 'hidden'
      }}
    >
      {() => (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', backgroundColor: 'var(--sl-bg-secondary, #FAFAFA)' }}>
          <div style={{ padding: '8px', borderBottom: '1px solid var(--sl-border-primary, #D5D7DA)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {hasUnsavedChanges && (
                <span style={{ fontSize: '12px', color: 'var(--sl-text-warning)', marginRight: '8px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <AlertTriangle width={14} height={14} aria-hidden="true" />
                  Unsaved changes
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {onDiscard ? (
                <Button variant="secondary" size="sm" onClick={onDiscard} disabled={isSaving}>
                  Discard changes
                </Button>
              ) : null}
              <Button
                variant="primary"
                size="sm"
                icon={isSaving ? undefined : Save01}
                onClick={onSaveAndExit}
                disabled={isSaving}
              >
                {isSaving ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <Spinner size="sm" color="currentColor" aria-label="Saving" />
                    Saving...
                  </span>
                ) : 'Save and Exit'}
              </Button>
            </div>
          </div>
          {notice}
          <div style={{ flex: '1 1 auto', overflow: 'hidden' }}>
            {secretKey ? (
            <SeatsioDesigner
              secretKey={secretKey}
              chartKey={chartKey}
              onChartUpdated={() => {
                setHasUnsavedChanges(true);
              }}
              onChartCreated={chartKey => {
                setHasUnsavedChanges(true);
                onChartCreated(chartKey);
              }}
              openLatestDrawing={!!chartKey}
              region="na"
              publishing={false}
              features={{
                disabled: ['publishing'],
                readOnly: readOnlyName ? ['chartName'] : []
              }}
            />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                {keyFailed ? 'The seat map designer could not be opened.' : <Spinner aria-label="Loading seat map designer" />}
              </div>
            )}
          </div>
        </div>
      )}
    </Popup>
  );
};

SeatingChartDesignerModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onChartCreated: PropTypes.func,
  onSaveAndExit: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  hasUnsavedChanges: PropTypes.bool,
  editorKeyUrl: PropTypes.string.isRequired,
  chartKey: PropTypes.string,
  readOnlyName: PropTypes.bool,
  setHasUnsavedChanges: PropTypes.func,
  onDiscard: PropTypes.func,
  notice: PropTypes.node,
  overlayZIndex: PropTypes.number,
};

SeatingChartDesignerModal.defaultProps = {
  onDiscard: null,
  notice: null,
  overlayZIndex: 1060,
};

export default SeatingChartDesignerModal;


